// GRW-35: the pursuit's Knowledge graph as a pursuit-filtered projection.
// Seeds are the entities the pursuit links (customer, contracting office,
// competitors, teaming partners, stakeholders, attendees, vehicle, claim
// subjects); the canvas shows them and 1–2 hops of neighbours from the
// govcon graph projection. "Everything" renders today's whole-tenant panel.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraActionList, SpecraButton, SpecraCard, SpecraCardContent, SpecraEmptyState, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraLink, SpecraOverlayDialog, SpecraPanelHeader, SpecraSectionHeader, SpecraSegmentedControl, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { registryHref } from '../Registries/registryHref';
import { GraphCanvas } from './GraphCanvas';
import { usePursuitGraph, useUpsertEntityEdge } from './graphHooks';
import { PursuitGraphAddDialog } from './PursuitGraphAddDialog';
const ROLE_LABEL = {
    customer: 'Customer',
    contracting: 'Contracting office',
    incumbent: 'Incumbent',
    competitor: 'Competitor',
    teaming: 'Teaming partner',
    stakeholder: 'Stakeholder',
    attendee: 'Met at a touchpoint',
    vehicle: 'Vehicle',
    event_attendee: 'Event attendee',
    event_host: 'Event host',
    claim_subject: 'Named in hearsay'
};
const KIND_HREF_KIND = {
    person: 'CONTACT',
    company: 'COMPANY',
    partner: 'COMPANY',
    gov_org: 'GOVERNMENT_ORG',
    vehicle: 'VEHICLE'
};
function seedRoles(s) {
    return s.seedRoles.map((r)=>ROLE_LABEL[r] ?? r).join(' · ');
}
/** "3 minutes ago"-style relative time; '' when unknown. */ export function relativeTime(iso, now = new Date()) {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const mins = Math.max(0, Math.round((now.getTime() - t) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}
export function PursuitGraphPanel({ captureProjectId, highlightRef, hasCustomer, hasVehicle, renderTenantPanel }) {
    const [view, setView] = useState('pursuit');
    const [hops, setHops] = useState(2);
    const [addOpen, setAddOpen] = useState(false);
    const [pendingEdge, setPendingEdge] = useState(null);
    const [edgeType, setEdgeType] = useState('parent');
    const [edgeSource, setEdgeSource] = useState('');
    const [edgeConfidence, setEdgeConfidence] = useState('0.5');
    const upsertEdge = useUpsertEntityEdge();
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const { data, isLoading } = usePursuitGraph(captureProjectId, hops, false, view === 'pursuit');
    const nodes = useMemo(()=>data?.graph?.nodes ?? [], [
        data
    ]);
    const edges = useMemo(()=>data?.graph?.edges ?? [], [
        data
    ]);
    const seeds = useMemo(()=>data?.seeds ?? [], [
        data
    ]);
    const pending = seeds.filter((s)=>!s.nodeId);
    const seedNodeIds = seeds.filter((s)=>s.nodeId).map((s)=>s.nodeId);
    const selectedNode = nodes.find((n)=>n.id === selectedNodeId) ?? null;
    const selectedSeed = selectedNode ? seeds.find((s)=>s.nodeId === selectedNode.id) : undefined;
    const mergeHref = selectedSeed?.href ?? (selectedNode?.ryuguRef ? registryHref({
        kind: KIND_HREF_KIND[selectedNode.nodeType] ?? 'COMPANY',
        id: selectedNode.ryuguRef
    }) ?? '' : '');
    const header = /*#__PURE__*/ _jsx(SpecraPanelHeader, {
        title: "Knowledge graph",
        subtitle: "People, companies, agencies and vehicles linked to this pursuit, and how they connect.",
        actions: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-wrap items-center gap-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Show",
                    size: "sm",
                    value: view,
                    options: [
                        {
                            value: 'pursuit',
                            label: 'This pursuit',
                            testId: Testids.captureGraph.scopePursuit
                        },
                        {
                            value: 'tenant',
                            label: 'Everything',
                            testId: Testids.captureGraph.scopeTenant
                        }
                    ],
                    onValueChange: setView
                }),
                view === 'pursuit' && /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Depth",
                    size: "sm",
                    value: String(hops),
                    options: [
                        {
                            value: '1',
                            label: '1 hop'
                        },
                        {
                            value: '2',
                            label: '2 hops'
                        }
                    ],
                    onValueChange: (v)=>setHops(v === '1' ? 1 : 2)
                }),
                view === 'pursuit' && /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    "data-testid": Testids.captureGraph.addEntityButton,
                    onClick: ()=>setAddOpen(true),
                    iconLeft: /*#__PURE__*/ _jsx(Plus, {
                        className: "h-4 w-4"
                    }),
                    children: "Add to this pursuit"
                })
            ]
        })
    });
    if (view === 'tenant') {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                header,
                renderTenantPanel()
            ]
        });
    }
    const addButton = /*#__PURE__*/ _jsx(SpecraButton, {
        size: "sm",
        onClick: ()=>setAddOpen(true),
        iconLeft: /*#__PURE__*/ _jsx(Plus, {
            className: "h-4 w-4"
        }),
        children: "Add to this pursuit"
    });
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.captureGraph.panelRoot,
        "data-scope": "pursuit",
        children: [
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3 pt-4",
                children: [
                    header,
                    data?.truncated && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "info",
                        variant: "framed",
                        "data-testid": Testids.captureGraph.truncated,
                        children: [
                            "Showing the closest ",
                            nodes.length,
                            " entities. Switch to 1 hop to see fewer."
                        ]
                    }),
                    isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                        size: "body-sm",
                        tone: "muted",
                        children: "Loading graph…"
                    }) : seeds.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        framed: true,
                        title: "Nothing linked yet",
                        description: "Add the customer, competitors, teaming partners and people to see how they connect.",
                        action: addButton
                    }) : /*#__PURE__*/ _jsxs("div", {
                        className: "grid gap-3 lg:grid-cols-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: pending.length > 0 ? 'lg:col-span-2' : 'lg:col-span-3',
                                children: nodes.length > 0 ? /*#__PURE__*/ _jsx(GraphCanvas, {
                                    nodes: nodes,
                                    edges: edges,
                                    highlightRef: highlightRef,
                                    seedNodeIds: seedNodeIds,
                                    selectedNodeId: selectedNodeId ?? undefined,
                                    onSelectNode: (n)=>setSelectedNodeId(n?.id ?? null),
                                    onSelectEdge: (_e)=>setSelectedNodeId(null),
                                    onConnectEdge: (from, to)=>{
                                        setPendingEdge({
                                            from,
                                            to
                                        });
                                        setEdgeType('parent');
                                        setEdgeSource('');
                                        setEdgeConfidence('0.5');
                                    },
                                    refitOnNodeChange: true
                                }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    size: "compact",
                                    title: "Not drawn yet",
                                    description: "The linked records appear here once the graph catches up."
                                })
                            }),
                            pending.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.captureGraph.pendingSeeds,
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                        variant: "group",
                                        title: "Not in the graph yet"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraActionList, {
                                        trailing: null,
                                        items: pending.map((s)=>({
                                                id: `${s.kind}:${s.registryId}`,
                                                label: s.label,
                                                description: seedRoles(s),
                                                actions: s.href ? /*#__PURE__*/ _jsx(SpecraLink, {
                                                    href: s.href,
                                                    renderLink: renderRouterLink,
                                                    children: "Open record"
                                                }) : undefined
                                            }))
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: [
                                            "The graph updates in the background",
                                            data?.projectedThrough ? ` (last updated ${relativeTime(data.projectedThrough)}).` : '.'
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    selectedNode && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 rounded-md border p-3",
                        "data-node-id": selectedNode.id,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                size: "body-sm",
                                children: selectedNode.displayName
                            }),
                            selectedSeed && /*#__PURE__*/ _jsx(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: seedRoles(selectedSeed)
                            }),
                            mergeHref && /*#__PURE__*/ _jsx(SpecraLink, {
                                href: mergeHref,
                                renderLink: renderRouterLink,
                                "data-testid": Testids.captureGraph.mergeInRegistries,
                                children: "Merge duplicates in Registries →"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                open: !!pendingEdge,
                onOpenChange: (open)=>{
                    if (!open) setPendingEdge(null);
                },
                title: "New relationship",
                testId: Testids.captureGraph.edgeCreateDialog,
                footerProps: {
                    confirmLabel: 'Add relationship',
                    cancelLabel: 'Cancel',
                    confirmDisabled: !edgeSource.trim(),
                    loading: upsertEdge.isPending,
                    loadingLabel: 'Adding…',
                    confirmProps: {
                        'data-testid': Testids.captureGraph.addEdgeSubmit
                    },
                    onCancel: ()=>setPendingEdge(null),
                    onConfirm: ()=>{
                        if (!pendingEdge || !edgeSource.trim()) return;
                        upsertEdge.mutate({
                            fromNode: pendingEdge.from,
                            toNode: pendingEdge.to,
                            edgeType: edgeType.trim() || 'parent',
                            source: edgeSource.trim(),
                            confidence: Math.min(1, Math.max(0, Number.parseFloat(edgeConfidence) || 0))
                        }, {
                            onSuccess: ()=>setPendingEdge(null)
                        });
                    }
                },
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Relationship",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: edgeType,
                            onChange: (e)=>setEdgeType(e.target.value),
                            "data-testid": Testids.captureGraph.addEdgeTypeInput
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Source",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: edgeSource,
                            onChange: (e)=>setEdgeSource(e.target.value),
                            "data-testid": Testids.captureGraph.addEdgeSourceInput
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Confidence (0–1)",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: edgeConfidence,
                            onChange: (e)=>setEdgeConfidence(e.target.value),
                            "data-testid": Testids.captureGraph.addEdgeConfidenceInput
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PursuitGraphAddDialog, {
                open: addOpen,
                onOpenChange: setAddOpen,
                captureProjectId: captureProjectId,
                hasCustomer: hasCustomer,
                hasVehicle: hasVehicle
            })
        ]
    });
}
function renderRouterLink({ href, children, ...rest }) {
    return /*#__PURE__*/ _jsx(Link, {
        to: href,
        ...rest,
        children: children
    });
}
